import React, { useEffect, useState } from "react";
import { Col, ConfigProvider, Row, Tabs } from "antd";
import { componentPalette } from "src/theme/component-palette";
import { profileStore } from "src/stores/profile-store";
import { useShallow } from "zustand/react/shallow";
import useNavigationMenu from "src/hook/useNavigationMenu";
import { Permission } from "src/entity/permission/permission";
import { getHistory, getOptionsFilter } from "src/services/flare-history.service";
import HistoryTable from "./components/HistoryTable";
import { HistoryData, OptionFilter } from "src/entity/history/history-entity";

const FlareHistory = () => {
  const profile = profileStore(useShallow((state) => state.profile));
  const { permissions } = useNavigationMenu();
  const [companyId, setCompanyId] = useState<string>("");
  const [historys, setHistorys] = useState<HistoryData[]>([]);
  const [activeKey, setActiveKey] = useState<string>("setup/heating-value");
  const [selectedMenu, setSelectedMenu] = useState<string>("setup/heating-value");
  const [loading, setLoading] = useState<boolean>(false);
  const [optionsFilter, setOptionFilter] = useState<OptionFilter[]>([]);
  const [historyFilter, setHistoryFilter] = useState<string>("");
  const [pageSize, setPageSize] = useState(10);
  const [current, setCurrent] = useState(1);
  const [total, setTotal] = useState(0);
  const [parrentKey, setParrentKey] = useState("setup");

  const calPagination = (current?: number) => {
    if (current) {
      return (current - 1) * 10;
    }
  };

  const createParamQuery = (): string => {
    let queryParam = `menu=${selectedMenu}`;
    let offset = 0;
    let limit = pageSize;

    if (current) {
      let resultCalOffset = calPagination(current);
      if (resultCalOffset) {
        offset = resultCalOffset;
      }
      queryParam += `&offset=${offset}`;
    }
    if (limit) {
      queryParam += `&limit=${limit}`;
    }

    if (historyFilter) {
      queryParam += historyFilter;
    }

    return queryParam;
  };

  const fetchOptionFilter = async () => {
    try {
      if (companyId && selectedMenu) {
        const query = `menu=${selectedMenu}`;
        const response = await getOptionsFilter(companyId, query);
        if (response.data.code === 200) {
          setLoading(false);
          const data = response.data.data.filter;
          setOptionFilter(data);
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      if (companyId && selectedMenu) {
        setLoading(true);
        const query = createParamQuery();
        const response = await getHistory(companyId, query);
        if (response.data.code === 200) {
          setLoading(false);
          const offset = calPagination(current);
          const data = response.data.data.history.map((el: any, index: any) => {
            return {
              no: (index + offset + 1).toString(),
              ...el,
            };
          });
          const total = response.data.data.total;
          setHistorys(data);
          setTotal(total);
        }
      } else {
        setHistorys([]);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const onChangeTable = (current: number, pageSize: number, query: string) => {
    setHistoryFilter(query);
    setCurrent(current);
    setPageSize(pageSize);
  };

  const onChangeTab = (key: string) => {
    setActiveKey(key);
    const tmpParrent = titleBar.find((item) => item.key === key);
    if (tmpParrent) {
      setParrentKey(tmpParrent.parent);
    }

    setSelectedMenu(key);
    setHistoryFilter("");
    setCurrent(1);
  };

  const checkPermissionViewTable = [Permission.V, Permission.VO, Permission.EA, Permission.EO].some((permission) =>
    permissions?.includes(permission),
  );

  const intitalData = () => {
    if (profile?.activeCompany?._id) {
      setCompanyId(profile.activeCompany._id);
    }
  };

  useEffect(() => {
    intitalData();
  }, []);

  useEffect(() => {
    setCurrent(1);
    intitalData();
  }, [profile?.activeCompany]);

  useEffect(() => {
    setHistoryFilter("");
  }, [selectedMenu]);

  useEffect(() => {
    fetchHistory();
    fetchOptionFilter();
  }, [companyId, selectedMenu, historyFilter]);

  const renderTable = () => {
    return (
      <HistoryTable
        selectedMenu={selectedMenu}
        historys={checkPermissionViewTable ? historys : []}
        loading={loading}
        optionsFilter={optionsFilter}
        total={total}
        companyId={companyId}
        onChangeTable={(current, pageSize, query) => onChangeTable(current, pageSize, query)}
      />
    );
  };

  const titleBar = [
    {
      parent: "setup",
      key: "setup",
      label: "Set up",
      type: "main",
      colSpan: "8",
    },
    {
      parent: "setup",
      key: "setup/heating-value",
      label: "Heating Value",
      type: "sub",
      colSpan: "4",
    },
    {
      parent: "setup",
      key: "setup/flare-volume",
      label: "Flare Volume",
      type: "sub",
      colSpan: "4",
    },
    {
      parent: "manual",
      key: "manual/manual-volume",
      label: "Flare Volume",
      type: "sub",
      colSpan: "6",
    },
    {
      parent: "manual",
      key: "manual",
      label: "Manual",
      type: "main",
      colSpan: "6",
    },
    {
      parent: "report",
      key: "report/report-flare-volume",
      label: "Flare Volume",
      type: "sub",
      colSpan: "3",
    },
    {
      parent: "report",
      key: "report/report-voc-emission",
      label: "VOCs Emission",
      type: "sub",
      colSpan: "3",
    },
    {
      parent: "report",
      key: "report/report-government",
      label: "รายงานราชการ",
      type: "sub",
      colSpan: "4",
    },
    {
      parent: "report",
      key: "report",
      label: "Report",
      type: "main",
      colSpan: "10",
    },
  ];

  const handleChangeTab = (tabKey: string) => {
    setSelectedMenu(tabKey);
    setHistoryFilter("");
    setCurrent(1);
    setActiveKey(tabKey);
  };

  return (
    <ConfigProvider
      theme={{
        components: {
          Tabs: {
            colorBgContainer: componentPalette.grey,
            itemSelectedColor: componentPalette.black,
          },
        },
      }}
    >
      <div>
        <div>
          <Row gutter={0} style={{ display: "flex", marginBottom: 16, width: "100%" }}>
            {titleBar.map((tab, index) => {
              let borderRadius = "";
              if (["setup", "setup/heating-value"].includes(tab.key)) {
                borderRadius = "50px 0 0 50px";
              } else if (["report", "report/government-report"].includes(tab.key)) {
                borderRadius = "0px 50px 50px 0px";
              } else {
                borderRadius = "0";
              }
              if (tab.type === "main" && !activeKey.includes(tab.key)) {
                return (
                  <Col span={tab.colSpan}>
                    <div
                      key={tab.key}
                      onMouseEnter={() => {
                        setActiveKey(tab.key);
                        setParrentKey(tab.parent);
                      }}
                      onClick={() => handleChangeTab(tab.key)}
                      style={{
                        cursor: "pointer",
                        padding: "8px 16px",
                        borderRadius: borderRadius,
                        backgroundColor: activeKey === tab.key ? componentPalette.grey : "rgba(0, 0, 0, 0.02)",
                        textAlign: "center",
                        paddingLeft: "50px",
                        border: "1px solid #d9d9d9",
                      }}
                    >
                      {tab.label}
                    </div>
                  </Col>
                );
              } else if (tab.type === "sub") {
                return activeKey === tab.parent || parrentKey === tab.parent ? (
                  <Col span={tab.colSpan}>
                    <div
                      key={tab.key}
                      onClick={() => handleChangeTab(tab.key)}
                      style={{
                        cursor: "pointer",
                        padding: "8px 16px",
                        flex: 1,
                        borderRadius: borderRadius,
                        textAlign: "center",
                        backgroundColor: activeKey === tab.key ? componentPalette.grey : "rgba(0, 0, 0, 0.02)",
                        border: "1px solid #d9d9d9",
                      }}
                    >
                      {tab.label}
                    </div>
                  </Col>
                ) : null;
              } else {
                return null;
              }
            })}
          </Row>
        </div>
      </div>
      {renderTable()}
    </ConfigProvider>
  );
};

export default FlareHistory;
